import React from 'react';
import Brick from './BrickCore';
import { FormsField, FormRenderer, TranslatableField } from '../../components';
import { FormLayout } from '..';
import { Utils } from '../../services';
import { normalizeFormLayoutSubmitValues } from '../../services/formLayoutGroupUtils';
import { Field, FieldArray } from 'redux-form';
import { Col, FormGroup, Label, FormText, InputGroup, InputGroupAddon, InputGroupText, Button } from 'reactstrap';
import {
    getWorkflowContextPathOptionGroups,
    pathSetFromContextPathGroups,
} from '../../containers/Workflows/workflowContextPathOptions';
import ContextPathSelect from '../../components/ContextPathSelect/ContextPathSelect';

const VALIDATION_OPS = [
    'eq',
    'neq',
    'gte',
    'lte',
    'gt',
    'lt',
    'in_list',
    'not_in_list',
    'exists',
    'not_exists',
    'is_true',
    'is_false',
    'truthy',
];

const validation_op_skips_value = (op) => {
    return op === 'exists' || op === 'not_exists' || op === 'is_true' || op === 'is_false' || op === 'truthy';
};

/** Class representing a workflow brick to
 * show a form.
 */
class FormShowBrick extends Brick {
    /**
     * @param {{ forms?: object[] }|null|undefined} detailCtx
     * @returns {string|null}
     */
    getNodeDetails(detailCtx) {
        const forms = detailCtx?.forms || [];
        const formId = this.getOption('form_id');
        if (formId == null)
            return null;

        const form = forms.find((f) => String(f?.id) === String(formId) || String(f?._id) === String(formId));
        const label = this.localizedDetailLabel(form?.name, detailCtx, '');
        return label || null;
    }

    render_submit_icon_field = ({ input, placeholder }) => {
        const icon_name = input.value || 'save';
        return (
            <InputGroup>
                <input
                    {...input}
                    type="text"
                    className="form-control"
                    placeholder={placeholder}
                />
                <InputGroupAddon addonType="append">
                    <InputGroupText>
                        <i className={`fa fa-${icon_name}`} aria-hidden="true" />
                    </InputGroupText>
                </InputGroupAddon>
            </InputGroup>
        );
    };

    renderValidationPathField = ({ input, contextPathGroups, knownContextPaths }) => {
        return (
            <ContextPathSelect
                groups={contextPathGroups}
                value={input.value}
                onChange={input.onChange}
                knownPaths={knownContextPaths}
                customPlaceholder="user.email"
            />
        );
    };

    render_validation_expressions = ({ fields, meta: _meta, t, context_path_groups, known_context_paths }) => {
        return (
            <div className="mt-2">
                {fields.map((name, index) => {
                    const expression = fields.get(index) || {};
                    const op = expression?.op || 'eq';
                    return (
                        <div key={name} className="border rounded p-2 mb-2">
                            <FormGroup className="mb-2">
                                <Label className="small mb-1">{t('workflows.bricks.form.show.options.validation_path')}</Label>
                                <Field
                                    name={`${name}.path`}
                                    component={this.renderValidationPathField}
                                    contextPathGroups={context_path_groups}
                                    knownContextPaths={known_context_paths}
                                />
                            </FormGroup>
                            <FormGroup className="mb-2">
                                <Label className="small mb-1">{t('workflows.bricks.form.show.options.validation_operator')}</Label>
                                <Field component="select" name={`${name}.op`} className="form-control">
                                    {VALIDATION_OPS.map((op_id) => (
                                        <option key={op_id} value={op_id}>
                                            {t(`workflows.graph.op.${op_id}`)}
                                        </option>
                                    ))}
                                </Field>
                            </FormGroup>
                            <FormGroup className="mb-2">
                                <Label className="small mb-1">{t('workflows.bricks.form.show.options.validation_value')}</Label>
                                <Field component="input" type="text" name={`${name}.value`} className="form-control" disabled={validation_op_skips_value(op)} />
                            </FormGroup>
                            <Button type="button" size="sm" color="danger" onClick={() => fields.remove(index)}>
                                <i className="fa fa-trash" aria-hidden="true" /> {t('common.delete')}
                            </Button>
                        </div>
                    );
                })}
                <Button
                    type="button"
                    size="sm"
                    color="info"
                    onClick={() => fields.push({ path: '', op: 'eq', value: '' })}
                >
                    <i className="fa fa-plus" aria-hidden="true" /> {t('workflows.bricks.form.show.options.validation_add_expression')}
                </Button>
            </div>
        );
    };

    /**
     * Get this brick options form
     *
     * @param {Object} props: an object with the following keys
     *                          - t
     *                          - workflow
     *                          - forms
     *                          - emails
     *                          - articles
     *                          - salepoints
     */
    getOptionsForm(props) {
        const { t, forms, workflow } = props;
        const forms_by_id = (forms || []).reduce((acc, f) => {
            if (f?.id != null) {
                acc[f.id] = f;
            }
            return acc;
        }, {});
        const context_path_groups = getWorkflowContextPathOptionGroups(workflow?.bricks || [], forms_by_id, {}, t);
        const known_context_paths = pathSetFromContextPathGroups(context_path_groups);

        if (!forms)
            return null;

        return (
            <>
                <FormGroup row>
                    <Label for="form_id" className="text-v-center" sm={12}>
                        { t('workflows.bricks.form.show.options.form_id') }
                    </Label>
                    <Col sm={12}>
                        <FormsField forms={forms} name={`bricks[${this.position}].options.form_id`} single />
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label className="text-v-center" sm={12}>
                        { t('workflows.bricks.form.show.options.show_form_name') }
                    </Label>
                    <Col sm={12}>
                        <Field component="select" name={`bricks[${this.position}].options.show_form_name`} className="form-control">
                            <option value="true">{ t('common.yes') }</option>
                            <option value="false">{ t('common.no') }</option>
                        </Field>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label className="text-v-center" sm={12}>
                        { t('workflows.bricks.form.show.options.submit_label') }
                    </Label>
                    <Col sm={12}>
                        <TranslatableField
                            name={`bricks[${this.position}].options.submit_label`}
                            component="input"
                            placeholder={t('workflows.bricks.form.show.options.submit_label_placeholder')}
                            optional
                        />
                    </Col>
                    <Col sm={12}>
                        <FormText color="muted">
                            { t('workflows.bricks.form.show.options.submit_label_help') }
                        </FormText>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label className="text-v-center" sm={12}>
                        { t('workflows.bricks.form.show.options.submit_icon') }
                    </Label>
                    <Col sm={12}>
                        <Field
                            name={`bricks[${this.position}].options.submit_icon`}
                            component={this.render_submit_icon_field}
                            placeholder={t('workflows.bricks.form.show.options.submit_icon_placeholder')}
                        />
                    </Col>
                    <Col sm={12}>
                        <FormText color="muted">
                            { t('workflows.bricks.form.show.options.submit_icon_help') }
                        </FormText>
                        <FormText>
                            <a
                                className="submit-icon-doc-link"
                                href="https://fontawesome.com/v4.7.0/icons/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                { t('workflows.bricks.form.show.options.submit_icon_doc_link') }
                            </a>
                        </FormText>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label className="text-v-center" sm={12}>
                        { t('workflows.bricks.form.show.options.validation') }
                    </Label>
                    <Col sm={12}>
                        <Field component="select" name={`bricks[${this.position}].options.submit_validation.operator`} className="form-control">
                            <option value="and">{ t('workflows.bricks.form.show.options.validation_and') }</option>
                            <option value="or">{ t('workflows.bricks.form.show.options.validation_or') }</option>
                        </Field>
                    </Col>
                    <Col sm={12}>
                        <FieldArray
                            name={`bricks[${this.position}].options.submit_validation.expressions`}
                            component={this.render_validation_expressions}
                            t={t}
                            context_path_groups={context_path_groups}
                            known_context_paths={known_context_paths}
                        />
                    </Col>
                    <Col sm={12}>
                        <FormText color="muted">
                            { t('workflows.bricks.form.show.options.validation_help') }
                        </FormText>
                    </Col>
                </FormGroup>
            </>
        );
    }

    /**
     * Run this brick
     *
     * @param {Object} context
     * @param {Boolean} isEmbedded
     * @param {Callable} onDone
     */
    run(context, isEmbedded, onDone) {
        if (!this.resources?.form)
            return null;

        const form = new FormLayout(this.resources.form);
        const submit_label_option = this.getOption('submit_label');
        const submit_icon = this.getOption('submit_icon') || 'save';
        const submit_validation = this.getOption('submit_validation') || null;
        const show_form_name = String(this.getOption('show_form_name', 'true')).toLowerCase() !== 'false';
        const submit_label = typeof submit_label_option === 'string'
            ? submit_label_option
            : Utils.localized_or_fallback(
                submit_label_option,
                context?.lang || Utils.default_lang(),
                'Enregistrer'
            );
        const submit = values => {
            context.forms = context.forms || {};
            context.forms[form.id] = normalizeFormLayoutSubmitValues(form, values);
            onDone(context, (_err, _workflow) => {});
        }

        return (
            <div className="FormShowBrick">
                <FormRenderer
                    formLayout={ form }
                    onSubmit={ submit }
                    isLoading={ false }
                    defaultValues={ context }
                    submitLabel={ submit_label }
                    submitIcon={ submit_icon }
                    submitValidation={ submit_validation }
                    showFormName={ show_form_name }
                    resources={ this.resources }
                />
            </div>
        );
    }
}

export default FormShowBrick;
